import type { PlanName } from "api/subscription/models/PlanName";
import type { ComponentType } from "react";
import { LoginPreview, RegistrationPreview, MarkdownPreview } from "screens/landing/tabs/admin/copies/components/CopyPreviews";

// Investment product option interfaces
export interface ProductOption {
  id: string;
  name: string;
  price: string;
  description?: string;
}

export interface DealPromoBanner {
  label: string;
  bgColor: string;
  textColor: string;
  tierFilter?: string;
}

export const defaultDealPromoBanner: DealPromoBanner = {
  label: "FREE",
  bgColor: "yellow.400",
  textColor: "black",
  tierFilter: "tier_0",
};

// Deal input suggestion interface
export interface DealInputSuggestion {
  key: string;
  question: string;
  label: string;
  type: "suggestion" | "quick_link";
  icon?: string;
  iconColor?: string;
}

// Deal filter action
export interface DealFilterAction {
  key: string;
  label: string;
  url: string;
  icon?: string;
  iconColor?: string;
}

// Hotlink action interface
export interface HotlinkAction {
  id: string;
  label: string;
  feedbackMessage: string;
  feedbackAction?: "thumbs_up" | "thumbs_down" | "unpinned" | "pinned";
  unpinReason?: string;
  className?: string;
}

// Pricing tier interfaces
export interface Feature {
  text: string;
  available?: boolean;
  description?: string;
}

export interface PricingTier {
  name: string;
  internalPlanName?: string;
  price: string | "Free" | "Contact For Pricing";
  originalPrice?: string;
  description: string;
  limitedTimeOffer?: string;
  dataSources?: Feature[];
  features: Feature[];
  buttonText: string;
  isPopular?: boolean;
  isCurrent?: boolean;
  canUpgradeTo?: string[];
}

export const tiers: PricingTier[] = [
  {
    name: "Starter",
    internalPlanName: "free_trial",
    price: "Free",
    description: "For everyone. Get started. No credit card required and realize the business benefits immediately",
    limitedTimeOffer: "Unlimited *Magnificent 7 Summary Analysis for all Plans. (*TSLA, GOOG, NVDA, MSFT, META, AAPL, AMZN)",
    dataSources: [
      { text: "Automatically collect and analyze information from SEC EDGAR & SEDAR", available: true },
      { text: "Augment research using web content and internet search", available: true },
      { text: "ETF and Mutual Funds", available: false },
      { text: "FactSet Data Feeds and Analysis", available: false },
    ],
    features: [
      { text: "Number of individual Equities Analyzed per Month", description: "2" },
      { text: "ETF, Mutual Funds and International Exchanges", available: false },
      { text: "Private Company Analysis", available: false },
      { text: "On Demand Additional Equities", description: "$20 per additional equity" },
      { text: "Data archiving for projects, questions and outcomes", description: "6 Months" },
      { text: "Portfolio Pi Quadrant Index Analysis", available: true },
      { text: "Advanced Portfolio Monitoring Charts", available: false },
    ],
    buttonText: "Free Reports",
    canUpgradeTo: ["professional", "business", "premium", "enterprise"],
  },
  {
    name: "Professional",
    internalPlanName: "professional",
    price: "$150",
    originalPrice: "As little as $9.40 per equity monitored",
    limitedTimeOffer: "Pay only $20 per month for the first 3 months, No Contract",
    description:
      "For Individual Investors, RIA's, Analysts AND small teams who want to automate research with deep financial data, Nasdaq, SEC, TMX, and SEDAR.",
    dataSources: [
      { text: "Automatically collect and analyze information from SEC EDGAR & SEDAR", available: true },
      { text: "Augment research using web content and internet search", available: true },
      { text: "ETF and Mutual Funds", available: true },
      { text: "FactSet Data Feeds and Analysis", available: false },
    ],
    features: [
      { text: "Number of individual Equities Analyzed per Month", description: "18 ($9.40 per equity)" },
      { text: "ETF, Mutual Funds and International Exchanges", available: true },
      { text: "Private Company Analysis", available: false },
      { text: "On Demand Additional Equities", description: "$15 per additional equity" },
      { text: "Data archiving for projects, questions and outcomes", description: "1 Year" },
      { text: "Portfolio Pi Quadrant Index Analysis", available: true },
      { text: "Advanced Portfolio Monitoring Charts", available: true },
    ],
    buttonText: "Upgrade Plan",
    isPopular: true,
    canUpgradeTo: ["business", "premium", "enterprise"],
  },
  {
    name: "Business",
    internalPlanName: "business",
    price: "$595",
    limitedTimeOffer: "Includes Unlimited Scheduled Reruns for projects for the first 3 months",
    description:
      "For small and mid-sized teams that want the power of combining qualitative and quantitative data. Includes advanced Portfolio Metrics and Analysis.",
    dataSources: [
      { text: "Automatically collect and analyze information from SEC EDGAR & SEDAR", available: true },
      { text: "Augment research using web content and internet search", available: true },
      { text: "ETF and Mutual Funds", available: true },
      { text: "FactSet Data Feeds and Analysis", description: "*Add-On" },
    ],
    features: [
      { text: "Number of individual Equities Analyzed per Month", description: "80 ($7.50 per equity)" },
      { text: "ETF, Mutual Funds and International Exchanges", available: true },
      { text: "Private Company Analysis", available: true },
      { text: "On Demand Additional Equities", description: "$10 per additional equity" },
      { text: "Data archiving for projects, questions and outcomes", description: "2 Years" },
      { text: "Portfolio Pi Quadrant Index Analysis", available: true },
      { text: "Advanced Portfolio Monitoring Charts", available: true },
    ],
    buttonText: "Contact Us",
    canUpgradeTo: ["premium", "enterprise"],
  },
  {
    name: "Enterprise",
    internalPlanName: "enterprise",
    price: "Contact For Pricing",
    limitedTimeOffer: "2 Free Custom Integrations, 2 Free Custom Templates",
    description:
      "For enterprise teams that have a greater need for a data feed, analysis integration customization, and private data sources for the deepest qualitative and quantitative data insights.",
    dataSources: [
      { text: "Automatically collect and analyze information from SEC EDGAR & SEDAR", available: true },
      { text: "Augment research using web content and internet search", available: true },
      { text: "ETF and Mutual Funds", available: true },
      { text: "FactSet Data Feeds and Analysis", description: "*Add-On" },
    ],
    features: [
      { text: "Number of individual Equities Analyzed per Month", description: "Custom" },
      { text: "ETF, Mutual Funds and International Exchanges", available: true },
      { text: "Private Company Analysis", available: true },
      { text: "On Demand Additional Equities", description: "Custom" },
      { text: "Data archiving for projects, questions and outcomes", description: "Custom" },
      { text: "Portfolio Pi Quadrant Index Analysis", available: true },
      { text: "Advanced Portfolio Monitoring Charts", available: true },
    ],
    buttonText: "Contact Us",
  },
];

export const productOptions: ProductOption[] = [
  {
    id: "scorecard",
    name: "Scorecard",
    price: "$50",
    description: "Scorecard",
  },
  {
    id: "report",
    name: "Investment Report",
    price: "$145",
    description: "Investment Report",
  },
  {
    id: "analysis",
    name: "Deep Analysis",
    price: "$325",
    description: "Deep Analysis",
  },
  {
    id: "credits",
    name: "Subscription Credits",
    price: "$5",
    description: "5 Credits",
  },
];

export interface PlaceholderCompany {
  ticker: string;
  securityType: string;
  companyName: string;
  logo: any;
  exchange: string;
  overview: string;
}

export const placeholderCompanies: PlaceholderCompany[] = [
  {
    logo: "",
    companyName: "Apple Inc",
    ticker: "AAPL",
    exchange: "NASDAQ",
    securityType: "equity",
    overview:
      "Apple Inc. is a multinational technology company that designs, manufactures, and markets consumer electronics, computer software, and online services. The company's products and services include iPhones, Mac computers, iPads, Apple Watches, Apple TVs, iOS, macOS, watchOS, and tvOS operating systems, as well as iCloud, Apple Music, Apple Pay, and other digital content and services.",
  },
  {
    logo: "",
    companyName: "Microsoft Corp",
    ticker: "MSFT",
    exchange: "NASDAQ",
    securityType: "equity",
    overview:
      "Microsoft Corporation is a multinational technology company that develops, manufactures, licenses, and supports a wide range of software products, services, and devices. The company's product offerings include operating systems, cross-device productivity applications, server applications, business solution applications, desktop and server management tools, software development tools, video games, and online advertising platforms. Microsoft also provides cloud-based computing services, artificial intelligence, and machine learning capabilities, as well as a range of services, including consulting, support, and training.",
  },
  {
    logo: "",
    companyName: "Nvidia Corp",
    ticker: "NVDA",
    exchange: "NASDAQ",
    securityType: "equity",
    overview:
      "NVIDIA Corporation is a leading technology company that designs, manufactures, and markets computer graphics interface and high-performance computing hardware, including graphics processing units (GPUs) and high-performance computing hardware, as well as provides related software and services, primarily serving the gaming, professional visualization, datacenter, and automotive markets.",
  },
];

// Icons from https://react-icons.github.io/react-icons/icons/md/
export const dealSuggestions: DealInputSuggestion[] = [
  {
    key: "suggestion_1",
    question: "AAPL",
    label: "Apple Inc. (NASDAQ:AAPL)",
    type: "suggestion",
  },
  {
    key: "suggestion_2",
    question: "Early stage financial services companies",
    label: "Early stage financial services companies",
    type: "suggestion",
  },
  {
    key: "quick_link_2",
    question: "AAPL",
    label: "Apple Inc.",
    type: "quick_link",
    icon: "MdOutlineLaptopMac",
    iconColor: "green.500",
  },
];

export const dealFilterActions: DealFilterAction[] = [
  {
    key: "gainers",
    label: "Gainers",
    url: "https://96db4f3a-7acc-47b1-b653-d3929f9bb137.trayapp.io/?trend=gainers",
    icon: "MdTrendingUp",
    iconColor: "green.500",
  },
  {
    key: "losers",
    label: "Losers",
    url: "https://96db4f3a-7acc-47b1-b653-d3929f9bb137.trayapp.io/?trend=losers",
    icon: "MdTrendingDown",
    iconColor: "red.500",
  },
];

export const tableThumbsUpHotlinkActions: HotlinkAction[] = [
  {
    id: "like-table-onpoint",
    label: "The extracted data is on point",
    feedbackMessage: "The extracted data is on point",
    feedbackAction: "thumbs_up",
    className: "ch-thumbs-up-hotlink-table-1",
  },
  {
    id: "like-table-expanded",
    label: "The extracted data is good but should be expanded",
    feedbackMessage: "The extracted data is good but should be expanded",
    feedbackAction: "thumbs_up",
    className: "ch-thumbs-up-hotlink-table-2",
  },
];

export const tableThumbsDownHotlinkActions: HotlinkAction[] = [
  {
    id: "unlike-table-erroneous-data",
    label: "The extracted data has erroneous information",
    feedbackMessage: "The extracted data has erroneous information",
    feedbackAction: "thumbs_down",
    className: "ch-thumbs-down-hotlink-table-1",
  },
  {
    id: "unlike-table-missing-data",
    label: "The extracted data is missing information",
    feedbackMessage: "The extracted data is missing information",
    feedbackAction: "thumbs_down",
    className: "ch-thumbs-down-hotlink-table-2",
  },
];

export const answerThumbsUpHotlinkActions: HotlinkAction[] = [
  {
    id: "like-answer",
    label: "I like this answer",
    feedbackMessage: "I like this answer",
    feedbackAction: "pinned",
    className: "ch-thumbs-up-hotlink-1",
  },
  {
    id: "good-qa-report",
    label: "Good research question and answer",
    feedbackMessage: "Good research question and answer",
    feedbackAction: "pinned",
    className: "ch-thumbs-up-hotlink-2",
  },
  {
    id: "insightful-prominent",
    label: "Insightful answer that should be prominent",
    feedbackMessage: "Insightful answer that should be prominent",
    feedbackAction: "pinned",
    className: "ch-thumbs-up-hotlink-3",
  },
  {
    id: "great-qa-reporting",
    label: "Great question and answer for reporting",
    feedbackMessage: "Great question and answer for reporting",
    feedbackAction: "pinned",
    className: "ch-thumbs-up-hotlink-4",
  },
  {
    id: "make-highlight",
    label: "Make this a highlight!",
    feedbackMessage: "Make this a highlight!",
    feedbackAction: "pinned",
    className: "ch-thumbs-up-hotlink-5",
  },
];

export const answerThumbsDownHotlinkActions: HotlinkAction[] = [
  {
    id: "erroneous-unpin",
    label: "Erroneous information, remove it and investigate",
    feedbackMessage: "Erroneous information, investigate and remove it",
    feedbackAction: "unpinned",
    unpinReason: "erroneous",
    className: "ch-thumbs-down-hotlink-1",
  },
  {
    id: "not-insightful-pinned",
    label: "Not that insightful, but keep it",
    feedbackMessage: "Not that insightful, but keep it",
    feedbackAction: "pinned",
    className: "ch-thumbs-down-hotlink-2",
  },
  {
    id: "repetitive-unpin",
    label: "Repetitive to other questions, remove it",
    feedbackMessage: "Repetitive to other questions, remove it",
    feedbackAction: "unpinned",
    unpinReason: "not good",
    className: "ch-thumbs-down-hotlink-3",
  },
  {
    id: "irrelevant-unpin",
    label: "Irrelevant answer for this research, remove it",
    feedbackMessage: "Irrelevant answer for this research, remove it",
    feedbackAction: "unpinned",
    unpinReason: "irrelevant",
    className: "ch-thumbs-down-hotlink-4",
  },
  {
    id: "dislike-question-unpin",
    label: "Not a great question, remove it",
    feedbackMessage: "Not a great question, remove it",
    feedbackAction: "unpinned",
    unpinReason: "remove question",
    className: "ch-thumbs-down-hotlink-5",
  },
];

export interface CopyPreviewProps {
  value: string | string[];
}

export type CopyPreviewComponent = ComponentType<CopyPreviewProps>;

export const copyPreviews: Partial<Record<CopyTypedKey | "markdown_preview", CopyPreviewComponent>> = {
  copy_login_heading: LoginPreview,
  copy_login_list_items: LoginPreview,
  copy_registration_heading: RegistrationPreview,
  copy_registration_subheading: RegistrationPreview,
  copy_registration_list_items: RegistrationPreview,
  copy_registration_footer_text: RegistrationPreview,
  copy_registration_cta: RegistrationPreview,
  copy_registration_heading_claim_company: RegistrationPreview,
  copy_registration_subheading_claim_company: RegistrationPreview,
  copy_registration_subheading_message_claim_company: RegistrationPreview,
  copy_registration_list_items_claim_company: RegistrationPreview,
  copy_registration_footer_text_claim_company: RegistrationPreview,
  copy_registration_cta_claim_company: RegistrationPreview,
  copy_registration_form_heading: RegistrationPreview,
  copy_registration_form_heading_claim_company: RegistrationPreview,
  copy_registration_form_subheading_claim_company: RegistrationPreview,
  copy_registration_submit_button: RegistrationPreview,
  copy_registration_form_title: RegistrationPreview,
  markdown_preview: MarkdownPreview,
};

export const CopiesKeys = [
  "copy_purchase_report_available_immediate",
  "copy_purchase_report_available_later",
  "copy_project_initializing_message",
  "copy_login_list_items",
  "copy_login_heading",
  "copy_registration_heading",
  "copy_registration_subheading",
  "copy_registration_list_items",
  "copy_registration_footer_text",
  "copy_registration_cta",
  "copy_registration_heading_claim_company",
  "copy_registration_subheading_claim_company",
  "copy_registration_subheading_message_claim_company",
  "copy_registration_list_items_claim_company",
  "copy_registration_footer_text_claim_company",
  "copy_registration_cta_claim_company",
  "copy_registration_form_heading",
  "copy_registration_form_heading_claim_company",
  "copy_registration_form_subheading_claim_company",
  "copy_registration_confirm_claim_company",
  "copy_registration_submit_button",
  "copy_registration_form_title",
  "copy_subscription_features",
  "copy_subscription_plan",
  "copy_subscription_plan_price",
  "copy_maintenance_banner",
  "copy_maintenance_banner_bg_color",
  "copy_maintenance_banner_text_color",
  "copy_ticker_placeholder_text",
  "copy_promotion_code",
  "copy_chart_quadrant_help",
  "copy_chart_buysellhold_help",
  "copy_chart_stockmarket_help",
  "copy_ticker_exchange_options",
  "copy_ticker_type_options",
  "copy_ticker_private_restricted_list",
  "copy_ticker_excluded_exchanges",
  "copy_ticker_excluded_types",
  "copy_ticker_type_order",
  "copy_ticker_filter_options",
  "copy_ticker_pricing_tiers",
  "copy_onboarding_quadrant_url",
  "copy_onboarding_video_url",
  "copy_ticker_upgrade_notification",
  "copy_default_deal_finder_symbols",
  "copy_project_placeholder_message",
  "copy_project_starting_message",
  "copy_investment_purchase_heading",
  "copy_markdown_investment_purchase_description",
  "copy_markdown_investment_purchase_description_anonymous",
  "copy_deal_due_diligence_description",
  "copy_deal_due_diligence_description_upgrade",
  "copy_deal_input_suggestions",
  "copy_deal_filter_actions",
  "copy_header_interaction_bar_placeholder",
  "copy_header_interaction_bar_placeholder_mobile",
  "copy_markdown_onboarding_step_one",
  "copy_markdown_onboarding_step_two_heading",
  "copy_markdown_onboarding_step_two",
  "copy_markdown_onboarding_step_three",
  "copy_version_tag",
  "copy_version_number",
  "copy_placeholder_tickers",
  "copy_information_extraction_format_options",
  "copy_deal_no_results_message",
  "copy_deal_reset_search_button",
  "copy_deal_finder_loading_messages",
  "copy_typing_text_loop_delays",
  "copy_deal_finder_purchase_message",
  "copy_deal_menu_more_like_this",
  "copy_deal_menu_show_competitors",
  "copy_deal_finder_action_buttons",
  "copy_deal_finder_premium_tiers",
  "copy_subscription_tiers",
  "copy_deal_analyze_company_description",
  "copy_deal_finder_free_label",
  "copy_placeholder_analysis_button_label",
  "copy_investment_purchase_button_label_register",
  "copy_investment_purchase_button_label_upgrade",
  "copy_websocket_disconnected_message",
  "copy_websocket_disconnected_message_details",
  "copy_thumbs_up_hotlink_actions_answers",
  "copy_thumbs_down_hotlink_actions_answers",
  "copy_thumbs_up_hotlink_actions_tables",
  "copy_thumbs_down_hotlink_actions_tables",
  "copy_deal_finder_analyze_option_subtitle",
  "copy_deal_finder_analyze_option_subtitle_free_tier",
  "copy_deal_finder_promotion_banner",
  "copy_report_purchase_coupon_code",
  "copy_deal_finder_ad_slot",
  "copy_deal_finder_ad_layout_key",
  "copy_markdown_new_version_message",
  "copy_markdown_deal_finder_quadrant_report_tooltip",
  "copy_deal_finder_rating_tooltip",
  "copy_markdown_investment_purchase_description_tier0",
] as const;
export type CopyTypedKey = typeof CopiesKeys[number];
export interface CopiesTypeMapping {
  copy_project_initializing_message: string;
  copy_login_list_items: string[];
  copy_login_heading: string;
  copy_registration_heading: string;
  copy_registration_subheading: string;
  copy_registration_list_items: string[];
  copy_registration_footer_text: string[];
  copy_registration_cta: string;
  copy_registration_heading_claim_company: string;
  copy_registration_subheading_claim_company: string;
  copy_registration_subheading_message_claim_company: string[];
  copy_registration_list_items_claim_company: string[];
  copy_registration_footer_text_claim_company: string[];
  copy_registration_cta_claim_company: string;
  copy_registration_form_heading: string;
  copy_registration_form_heading_claim_company: string;
  copy_registration_form_subheading_claim_company: string;
  copy_registration_confirm_claim_company: string;
  copy_registration_submit_button: string;
  copy_registration_form_title: string;
  copy_subscription_features: string[];
  copy_subscription_plan: PlanName;
  copy_subscription_plan_price: string;
  copy_maintenance_banner: string;
  copy_maintenance_banner_bg_color: string;
  copy_maintenance_banner_text_color: string;
  copy_ticker_placeholder_text: string;
  copy_promotion_code: string;
  copy_chart_quadrant_help: [string, string];
  copy_chart_buysellhold_help: [string, string];
  copy_chart_stockmarket_help: [string, string];
  copy_ticker_exchange_options: string[];
  copy_ticker_type_options: string[];
  copy_ticker_private_restricted_list: string[];
  copy_ticker_excluded_exchanges: string[];
  copy_ticker_excluded_types: string[];
  copy_ticker_type_order: string[];
  copy_ticker_filter_options: string[];
  copy_ticker_pricing_tiers: string[];
  copy_onboarding_quadrant_url: string;
  copy_onboarding_video_url: string;
  copy_ticker_upgrade_notification: string;
  copy_default_deal_finder_symbols: string[];
  copy_project_placeholder_message: string;
  copy_project_starting_message: string;
  copy_investment_purchase_heading: string;
  copy_markdown_investment_purchase_description: string;
  copy_markdown_investment_purchase_description_anonymous: string;
  copy_deal_due_diligence_description: string;
  copy_deal_due_diligence_description_upgrade: string;
  copy_deal_input_suggestions: string[];
  copy_deal_filter_actions: string[];
  copy_header_interaction_bar_placeholder: string;
  copy_header_interaction_bar_placeholder_mobile: string;
  copy_markdown_onboarding_step_one: string;
  copy_markdown_onboarding_step_two_heading: string;
  copy_markdown_onboarding_step_two: string;
  copy_markdown_onboarding_step_three: string;
  copy_version_tag: string;
  copy_version_number: string;
  copy_placeholder_tickers: string[];
  copy_information_extraction_format_options: string[];
  copy_deal_no_results_message: string;
  copy_deal_reset_search_button: string;
  copy_deal_finder_loading_messages: string[];
  copy_typing_text_loop_delays: string[];
  copy_purchase_report_available_immediate: string;
  copy_purchase_report_available_later: string;
  copy_deal_finder_purchase_message: string;
  copy_deal_menu_more_like_this: string;
  copy_deal_menu_show_competitors: string;
  copy_deal_finder_action_buttons: string;
  copy_deal_finder_premium_tiers: string[];
  copy_deal_analyze_company_description: string;
  copy_subscription_tiers: string[];
  copy_deal_finder_free_label: string;
  copy_placeholder_analysis_button_label: string;
  copy_investment_purchase_button_label_register: string;
  copy_investment_purchase_button_label_upgrade: string;
  copy_websocket_disconnected_message: string;
  copy_websocket_disconnected_message_details: string;
  copy_thumbs_up_hotlink_actions_answers: string[];
  copy_thumbs_down_hotlink_actions_answers: string[];
  copy_thumbs_up_hotlink_actions_tables: string[];
  copy_thumbs_down_hotlink_actions_tables: string[];
  copy_deal_finder_analyze_option_subtitle: string;
  copy_deal_finder_analyze_option_subtitle_free_tier: string;
  copy_report_purchase_coupon_code: string;
  copy_deal_finder_promotion_banner: string[];
  copy_deal_finder_ad_slot: string;
  copy_deal_finder_ad_layout_key: string;
  copy_markdown_new_version_message: string;
  copy_markdown_deal_finder_quadrant_report_tooltip: string;
  copy_deal_finder_rating_tooltip: string;
  copy_markdown_investment_purchase_description_tier0: string;
}

export type CopyValueMap = {
  [key in CopyTypedKey]: CopiesTypeMapping[key];
};

export type CopyValueType<K extends keyof CopyValueMap> = CopyValueMap[K];

export const defaultCopiesValues: CopiesTypeMapping = {
  copy_deal_finder_promotion_banner: [JSON.stringify(defaultDealPromoBanner)],
  copy_purchase_report_available_immediate:
    "The report is available for download immediately by clicking below. You will also see a list of Purchased Reports on your home page.",
  copy_purchase_report_available_later:
    "The report will be generated and emailed to you when ready. You will not be billed until the report is generated. You will also be able to see your Purchased Reports on the home page when they are generated and ready for download",
  copy_project_initializing_message: "Initializing project...",
  copy_login_list_items: [
    "Portfolio status at a glance with the Pi Quadrant Index | A powerful investment analysis tool available directly in your analysis portfolios.",
    "Private Company analysis is now available | Interested in the Pre-IPO market or accelerating a Private-to-Public Strategy? Get the right insights.",
    "Portfolio Sharing & Collaboration | A great way for analysts to collaborate on equity research and share results.",
  ],
  copy_login_heading: "Market Research & Analysis on AutoPilot",
  copy_registration_heading: "Ready to Analyze your Investments?",
  copy_registration_cta: "Register to Get Started",
  copy_registration_subheading: "What to Expect",
  copy_registration_list_items: [
    "Register with Charli | Complete the registration form and receive a free secure account to manage your investment analysis.",
    "Enter a Stock for Charli to Analyze | Pick a stock or ticker that is important for your portfolio that Charli can research and analyze.",
    "Explore Shared Portfolios | While Charli researches your equity you can explore the shared research and discover the power of the Pi Quadrant Index.",
    "Review your First Analysis | Charli will email you a report on the stock you selected and you can review the details in the app and dive deeper.",
    "Schedule and Automate Analysis | Upgrade to Professional to set custom schedules for your portfolio analysis.",
  ],
  copy_registration_footer_text: [
    "Charli is secure, trusted and used by thousands of investors worldwide. Charli works with top tier partners and providers, including national exchanges and regulators for data that is accurate, comprehensive, and timely.",
    "**There is no credit card and no contract required. The Starter package allows you to explore the features for free and get access to free reports. Upgrade to Professional for access to many advanced features and automation.",
  ],
  copy_registration_heading_claim_company: "Access the Charli IR Dashboard",
  copy_registration_cta_claim_company: "Why Register?",
  copy_registration_subheading_claim_company: "",
  copy_registration_subheading_message_claim_company: [
    "Raise capital faster. Tell your story better. Own your investor narrative.",
    "The Charli IR Dashboard gives executives and founders the tools to control and elevate how their company shows up to investors—powered by the same AI platform trusted by institutional capital allocators.",
  ],
  copy_registration_list_items_claim_company: [
    "Register and Create Your Account | Set up your secure Charli account—it's free to start and takes just a few minutes.",
    "Submit Basic Company Details | We'll ask for your company name, website, LinkedIn profile, address, and other information that helps us verify your company's identity.",
    "Add Primary Contact Info | Provide your name, email, and phone number. To speed up verification, we recommend using a business email that matches your company's domain.",
    "Verification & Access | Our team will quickly review and verify your submission. Once approved, you'll receive full access to your company's IR Dashboard and can start shaping your investment profile immediately.",
  ],
  copy_registration_footer_text_claim_company: [
    "The Charli IR Dashboard gives you: ",
    "Investor Readiness Scorecard - See where you stand and how to improve.",
  ],
  copy_registration_form_heading: "Start your Automated Analysis:",
  copy_registration_form_heading_claim_company: "Put Your Investment Story to Work:",
  copy_registration_form_subheading_claim_company:
    "All information provided is secured and private.  This information will not be used or presented without permission.  Charli uses this information to verify the company and grant access to manage the company's online profile, content, and narrative with the investors.",
  copy_registration_confirm_claim_company:
    "Confirm the submission. The primary contact person will be contacted by our team to verify the details and enable access to the company profile on the IR Dashboard. You will be notified shortly.",
  copy_registration_submit_button: "Register for Free",
  copy_registration_form_title: "Start Automating Your Investment Analysis:",
  copy_ticker_placeholder_text: "Enter the stock or company that you would like me to analyze",
  copy_subscription_features: [
    "Access to trusted FactSet Data | true | false | true | true | true",
    "Access to Nasdaq Real Time Data | true | false | true | true | true",
    "Automatically collect and analyze information from SEC, EDGAR & SEDAR | true | true | true | true | true",
    "Augment research using web content and internet search | true | true | true | true | true",
    "Number of Concurrent Equity Research Reports Analysis | 2 | 6 | 10 | 20 | Custom",
    "Number of individual Equity Research Reports per month | 3 | 15 | 40 | 100 | Custom",
    "Number of Easy Project Reruns and re-analysis per project per month | 0 | 1 | 4 | 10 | Custom",
    "Number of Personalized Research Questions per month | 45 | 225 | 600 | 1500 | Custom",
    "Data archiving for projects, questions and outcomes | 6 Months | 6 Months | 1 Year | 3 Years | Custom",
  ],
  copy_subscription_plan: "professional",
  copy_subscription_plan_price: "99",
  copy_maintenance_banner: "",
  copy_maintenance_banner_bg_color: "orange",
  copy_maintenance_banner_text_color: "gray.800",
  copy_promotion_code: "",
  copy_chart_quadrant_help: [
    "Learn more about the Portfolio Investment (Pi) Quadrant Chart",
    "https://charliai.com/wp-content/uploads/2025/01/Charli-AI-Pi-Quadrant-Index-.pdf",
  ],
  copy_chart_buysellhold_help: ["", ""],
  copy_chart_stockmarket_help: ["", ""],
  copy_ticker_exchange_options: ["NYSE", "NASDAQ", "TSX", "BATS", "NYSEARCA", "PRIVATE", "PRIVATE.US", "LON", "SWX", "ETR", "EPA", "TSXV"],
  copy_ticker_type_options: ["equity", "etf", "mutual_funds"],
  copy_ticker_private_restricted_list: ["SHEERID", "SPACEX", "GLEAN", "DATABRICKS", "CLOUDERA"],
  copy_ticker_excluded_exchanges: ["LON", "SWX", "ETR", "EPA", "TSXV"],
  copy_ticker_excluded_types: ["etf", "mutual_funds"],
  copy_ticker_type_order: ["equity"],
  copy_ticker_filter_options: ["equity", "etf", "mutual_funds", "private"],
  copy_ticker_pricing_tiers: ["tier_0", "tier_1", "tier_2", "tier_3", "tier_4", "tier_5"],
  copy_onboarding_quadrant_url: "https://support.charli.ai/",
  copy_onboarding_video_url: "https://demo.arcade.software/4Gg1BKDswzBUrPAZcJ4L?&embed_desktop=inline&playInline=true&preventNewTab=true",
  copy_ticker_upgrade_notification:
    "Please select a ticker from the available list. The ticker you selected is only available with an upgraded plan.",
  copy_default_deal_finder_symbols: [
    "JPM:NYSE",
    "BAC:NYSE",
    "AVGO:NASDAQ",
    "PLTR:NASDAQ",
    "LMT:NYSE",
    "HON:NASDAQ",
    "INTC:NASDAQ",
    "AMD:NASDAQ",
    "ARM:NASDAQ",
    "TD:TSX",
    "BMO:TSX",
    "COST:NASDAQ",
    "WMT:NYSE",
    "HD:NYSE",
    "F:NYSE",
    "GM:NYSE",
  ],
  copy_project_placeholder_message:
    "Add a new project to get started. You can add a new project by clicking the button to the left. This will open the Deal Finder where you can analyze an investment by entering a company ticker or an deal search query.",
  copy_project_starting_message:
    "I am collecting the data and running a full analysis on this company. I will send you an email with the summary report shortly. Once complete you can ask detailed questions and dive deeper. You can kick-off multiple projects at the same time and I will work on all of them in parallel.",
  copy_investment_purchase_heading: "PURCHASE INVESTMENT SCORECARD & REPORT",
  copy_markdown_investment_purchase_description:
    "Charli provides access to **investment scorecards**, **executive summary reports**, **detailed reports** and features that allow further deep analysis.\n\nSubscriptions provide access to these features as well as:\n- Custom portfolios\n- Additional market insights\n- Discounted credits",
  copy_markdown_investment_purchase_description_anonymous:
    "Upgrade with a free account to purchase investment scorecards, executive summary reports, detailed reports and features that allow further deep analysis.\n\n**No credit card and no contract required**",
  copy_deal_due_diligence_description:
    "Charli can analyze this stock and provide an executive summary report as well as allow you to do a deeper analysis.  Click 'Analyze Now' to start the analysis and when complete, Charli will email you a report.  This can take several minutes depending on how busy the system is.",
  copy_deal_due_diligence_description_upgrade:
    "You have selected a company this is not included in your plan. Click 'Upgrade' to review our plans and proceed with an analysis.",
  copy_deal_input_suggestions: dealSuggestions.map((option) => JSON.stringify(option)),
  copy_deal_filter_actions: dealFilterActions.map((option) => JSON.stringify(option)),
  copy_subscription_tiers: tiers.map((option) => JSON.stringify(option)),
  copy_header_interaction_bar_placeholder: "Search private and public companies for investment insights",
  copy_header_interaction_bar_placeholder_mobile: "Search for deals",
  copy_markdown_onboarding_step_one:
    "<h1 style='font-size: 1rem; margin: 0.5rem 0;'>Try Searching for Stocks</h1>\n\nThe copy you see here is a placeholder. Our wonderful delivery team will figure out what to write here.\n\nWe can have as many paragraphs as we want, its all in Delivery Copy. Just update copy_markdown_onboarding_step_one.",
  copy_markdown_onboarding_step_two_heading:
    "<h1 style='font-size: 1rem; margin-left: 1rem; margin-top: 0.5rem'>Check out the Interactive Video</h1>",
  copy_markdown_onboarding_step_two:
    "Charli will email you a summary report as soon as the research is complete.\n- Start new research right away, Charli can work on many at the same time\n- You can also check out the free shared research on the side panel",
  copy_markdown_onboarding_step_three:
    "<h1 style='font-size: 1rem; margin-bottom: 1rem'>Mouseover the Pi Quadrant Index for Instant Insights</h1>\n\nThe Pi Quadrant Index is a powerful tool used by top-tier investment advisors to understand how their portfolio and investments are doing in the market.\n\nBy leveraging the advanced models and analytics from our AI Labs, the Pi Quadrant Index evaluates all investments across 14 dimensions including the fundamentals of the company, sector analysis, market perception, analyst commentary, and competitive positioning.",
  copy_version_tag: "",
  copy_version_number: "Version 6.1",
  copy_placeholder_tickers: placeholderCompanies.map((option) => JSON.stringify(option)),
  copy_information_extraction_format_options: [
    "decimal:0",
    "decimal:2",
    "comma-separated",
    "short-scale",
    "percentage",
    "currency:USD",
    "date:%Y-%m-%d",
  ],
  copy_deal_no_results_message: "No deals match your search criteria, click the button below to reset the search and try again.",
  copy_deal_reset_search_button: "Reset the search",
  copy_deal_finder_loading_messages: [
    "Searching for related deals...",
    "Finding top deals in sector...",
    "Cross-referencing opportunities...",
    "Almost there...",
  ],
  copy_typing_text_loop_delays: ["1500", "3000"],
  copy_deal_finder_purchase_message:
    "Charli will email a copy of the PDF documents for investment scorecards and investment memos. Your credit card will not be charged until the PDF documents are emailed. You will also be provided with a link to download the PDF files.",
  copy_deal_menu_more_like_this: "Find deals similar to",
  copy_deal_menu_show_competitors: "Competitors to",
  copy_deal_finder_action_buttons: "quick_links",
  copy_deal_finder_premium_tiers: ["tier_4", "tier_5"],
  copy_deal_analyze_company_description: "Advanced insights and detailed recommendations",
  copy_deal_finder_free_label: "Free",
  copy_placeholder_analysis_button_label: "Free Analysis on",
  copy_investment_purchase_button_label_register: "REGISTER",
  copy_investment_purchase_button_label_upgrade: "UPGRADE",
  copy_websocket_disconnected_message: "Charli is trying to connect, requests can not be sent yet.",
  copy_websocket_disconnected_message_details:
    "Charli relies on standard and secure WebSockets for communication with the cloud services and the AI system. <br>Charli cannot currently connect with the cloud services, and this might be blocked due to your network firewall policies or ad blockers in your browser. Please check with your IT organization or network administrator to have WebSockets enabled for charliai.com.<br>You can also contact our <a href='https://support.charli.ai/hc/en-us/articles/33739429556877--Charli-is-trying-to-connect-information-message' target='_blank'>Customer Service</a> team for troubleshooting.",
  copy_thumbs_up_hotlink_actions_answers: answerThumbsUpHotlinkActions.map((option) => JSON.stringify(option)),
  copy_thumbs_down_hotlink_actions_answers: answerThumbsDownHotlinkActions.map((option) => JSON.stringify(option)),
  copy_thumbs_up_hotlink_actions_tables: tableThumbsUpHotlinkActions.map((option) => JSON.stringify(option)),
  copy_thumbs_down_hotlink_actions_tables: tableThumbsDownHotlinkActions.map((option) => JSON.stringify(option)),
  copy_deal_finder_analyze_option_subtitle: "",
  copy_deal_finder_analyze_option_subtitle_free_tier: "*for access to restricted deals",
  copy_report_purchase_coupon_code: "",
  copy_deal_finder_ad_slot: "6832660715",
  copy_deal_finder_ad_layout_key: "-75+ek+1c-3n+2y",
  copy_markdown_new_version_message:
    "This update adds a number of exciting new features as well some minor bug fixes and performance improvements.",
  copy_markdown_deal_finder_quadrant_report_tooltip: "",
  copy_deal_finder_rating_tooltip: "",
  copy_markdown_investment_purchase_description_tier0:
    "Charli provides access to **investment scorecards**, **executive summary reports**, **detailed reports** and features that allow further deep analysis.\n\nSubscriptions provide access to these features as well as:\n- Custom portfolios\n- Additional market insights\n- Discounted credits\n\n**Special tier 0 pricing available**",
};

export function isDefinedCopyKey(key: string): key is CopyTypedKey {
  return (CopiesKeys as readonly string[]).includes(key);
}
